import type {
	ListedPlayerStateForSchema,
	WithdrawalState,
	getAdminPaymentsFacetsOutput,
	getPlayersAuctionFacetsInput,
	getPoPaymentsFacetsOutput,
} from "@/api/custom_types";
import type { DateRange } from "react-day-picker";
import z from "zod";

const dateFilterSchema = z.object({
	from: z.string(),
	to: z.string(),
});

export type ParsedDateRange = {
	from: string;
	to: string;
};

export function parseDateRange(value?: string): ParsedDateRange | undefined {
	if (!value) return undefined;

	const parsedJson = JSON.parse(value);

	const result = dateFilterSchema.safeParse(parsedJson);
	if (!result.success) return undefined;

	const from = new Date(result.data.from);
	const to = new Date(result.data.to);

	if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
		return undefined;
	}

	return {
		from: from.toISOString(),
		to: to.toISOString(),
	};
}

export function buildFacetsFilter({
	searchQuery,
	dateFilter,
	dateRangeFilter,
	statusFilter,
	withdrawalFilter,
}: {
	searchQuery?: string;
	dateFilter?: string;
	dateRangeFilter?: DateRange;
	statusFilter?: string;
	withdrawalFilter?: string;
}): getPlayersAuctionFacetsInput {
	const filters: getPlayersAuctionFacetsInput = {};

	if (searchQuery?.trim()) {
		filters.search_field = searchQuery.trim();
	}

	if (dateFilter) {
		filters.start_datetime = dateFilter;
	}

	if (dateRangeFilter) {
		filters.start_datetime = dateRangeFilter.from;
		filters.end_datetime = dateRangeFilter.to;
	}

	if (statusFilter) {
		filters.listed_player_states = [statusFilter as ListedPlayerStateForSchema];
	}

	if (withdrawalFilter) {
		const withdrawalArray = parseCommaSeparatedString(withdrawalFilter);
		if (withdrawalArray.length > 0) {
			filters.withdrawal_states = withdrawalArray as WithdrawalState[];
		}
	}

	return filters;
}

/**
 * Parses a comma-separated string into an array of non-empty trimmed values
 * @param value - Comma-separated string (e.g., "value1,value2,value3")
 * @returns Array of trimmed non-empty strings
 */
export function parseCommaSeparatedString(value?: string): string[] {
	if (!value?.trim()) return [];
	return value
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
}

/**
 * Converts POPaymentFacets to format expected by useFilterFacets hook
 * @param facets - The facets data from the API
 * @param useWithFilter - If true, uses with_filter count, otherwise uses without_filter count
 * @returns Formatted facets data compatible with useFilterFacets
 */
export function convertPaymentFacetsToFilterFormat(
	facets: getPoPaymentsFacetsOutput | undefined,
	useWithFilter = false,
): {
	withdrawal_states: [string, number][];
	bank_names: [string, number][];
} {
	if (!facets) {
		return { withdrawal_states: [], bank_names: [] };
	}

	return {
		withdrawal_states: facets.withdrawal_states.map(([status, item]) => [
			status,
			useWithFilter ? item.with_filter : item.without_filter,
		]),
		bank_names: facets.bank_names.map(([bank, item]) => [
			bank,
			useWithFilter ? item.with_filter : item.without_filter,
		]),
	};
}

/**
 * Converts AdminPaymentFacets to format expected by useFilterFacets hook
 * @param facets - The facets data from the API
 * @param useWithFilter - If true, uses current filtered facets, otherwise uses initial facets
 * @returns Formatted facets data compatible with useFilterFacets
 */
export function convertAdminPaymentFacetsToFilterFormat(
	facets: getAdminPaymentsFacetsOutput | undefined,
	useWithFilter = false,
): {
	withdrawal_states: [string, number][];
	bank_names: [string, number][];
	payment_operators: [string, number][];
} {
	if (!facets) {
		return {
			withdrawal_states: [],
			bank_names: [],
			payment_operators: [],
		};
	}

	return {
		withdrawal_states: facets.withdrawal_states.map(([status, item]) => [
			status,
			useWithFilter ? item.with_filter : item.without_filter,
		]),
		bank_names: facets.bank_names.map(([bank, item]) => [
			bank,
			useWithFilter ? item.with_filter : item.without_filter,
		]),
		payment_operators: facets.payment_operators.map(([id, , count]) => [
			id,
			count,
		]),
	};
}
